import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, CreditCard, Loader2, BookOpen, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { money } from "./shared";

export function PortalSidebar({ booking, payInfo, paymentHistory, payForm, setPayForm, reportPayment, documents }) {
  const quoteHref = `${api.defaults.baseURL}/portal/quote-pdf`;
  const [balancePaying, setBalancePaying] = useState(false);
  const depositBalance = Number(booking.deposit_balance_due || 0);
  const bookingFee = Number(booking.booking_fee || 0);
  const fullDeposit = Number(booking.deposit_amount || booking.deposit_total || 0);
  const payDepositBalance = async () => {
    setBalancePaying(true);
    try {
      const r = await api.post("/payments/deposit-balance-checkout", { reference: booking.reference, origin: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start balance checkout"); setBalancePaying(false); }
  };
  return (
    <div className="space-y-6">
      <div className="de-card p-6">
        <div className="label-eyebrow mb-3">Booking summary</div>
        <div className="text-sm space-y-1">
          <div><b>Start date:</b> {booking.start_date}</div>
          <div><b>Total (inc. VAT):</b> £{Math.round(booking.total_inc_vat||0).toLocaleString("en-GB")}</div>
          {bookingFee > 0 && <div><b>Booking fee paid:</b> £{Math.round(bookingFee).toLocaleString("en-GB")}</div>}
          {depositBalance > 0 && <div><b>Deposit balance due:</b> <span className="text-[#C79A3B] font-bold">£{Math.round(depositBalance).toLocaleString("en-GB")}</span></div>}
          <div><b>Status:</b> {booking.status}</div>
        </div>
        <a data-testid="portal-download-quote" href={quoteHref} target="_blank" rel="noreferrer"
          className="de-btn-secondary w-full justify-center mt-4 inline-flex items-center gap-2">
          <Download size={15} /> Download quote PDF
        </a>
      </div>

      {depositBalance > 0 && (
        <div className="de-card p-6 border-[#C79A3B]" data-testid="portal-deposit-balance-card">
          <div className="label-eyebrow mb-2 text-[#C79A3B]">Deposit balance due</div>
          <p className="text-sm text-[#6B7280] mb-3">Once your on-site survey is completed and you&rsquo;re happy to proceed, the remaining <b>£{Math.round(depositBalance).toLocaleString("en-GB")}</b> of your 10% deposit is due (booking fee of £{Math.round(bookingFee).toLocaleString("en-GB")} already paid; full deposit £{Math.round(fullDeposit).toLocaleString("en-GB")}).</p>
          <button data-testid="portal-pay-deposit-balance" onClick={payDepositBalance} disabled={balancePaying}
            className="de-btn-primary w-full justify-center disabled:opacity-60">
            {balancePaying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Pay balance by card &mdash; £{Math.round(depositBalance).toLocaleString("en-GB")}
          </button>
          <p className="text-[11px] text-[#9CA3AF] mt-2 text-center">Prefer bank transfer? Use the &ldquo;Record a payment&rdquo; box below.</p>
        </div>
      )}

      <div className="de-card p-6">
        <div className="label-eyebrow mb-3">Payment schedule</div>
        <ul className="text-sm space-y-1 text-[#6B7280]">
          <li>£499 booking fee &mdash; reserves your date &amp; survey</li>
          <li>Balance of 10% deposit &mdash; due once your survey is signed off</li>
          <li>85% across 14 weekly payments during build</li>
          <li>5% one week after completion</li>
          <li className="text-xs">No card or payment surcharges. Material items (windows, rooflights) may require up-front payment.</li>
        </ul>
      </div>

      <div className="de-card p-6" data-testid="portal-payments-card">
        <div className="label-eyebrow mb-3">Your payments</div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="border border-[#E5E7EB] p-3"><div className="label-eyebrow">Confirmed paid</div><div className="text-lg font-black" data-testid="portal-paid-to-date">{money(payInfo.paid_to_date)}</div></div>
          <div className="border border-[#C79A3B] bg-[#FBF3EC] p-3"><div className="label-eyebrow">Balance</div><div className="text-lg font-black" data-testid="portal-balance">{money(payInfo.balance)}</div></div>
        </div>

        <div className="border-t border-[#E5E7EB] pt-4">
          <div className="font-bold text-sm mb-2">Record a payment you&apos;ve made</div>
          <p className="text-xs text-[#6B7280] mb-3">Made a bank transfer or payment to us? Log it here and we&apos;ll reconcile and confirm it.</p>
          <input data-testid="portal-pay-amount" type="number" value={payForm.amount} onChange={e=>setPayForm({...payForm, amount:e.target.value})} placeholder="Amount £" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm mb-2"/>
          <select data-testid="portal-pay-method" value={payForm.method} onChange={e=>setPayForm({...payForm, method:e.target.value})} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm mb-2 bg-white">
            {["bacs","cash","cheque","card"].map(m=><option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
          <input data-testid="portal-pay-date" type="date" value={payForm.paid_at} onChange={e=>setPayForm({...payForm, paid_at:e.target.value})} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm mb-2"/>
          <input data-testid="portal-pay-ref" value={payForm.payment_ref} onChange={e=>setPayForm({...payForm, payment_ref:e.target.value})} placeholder="Your payment reference (optional)" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm mb-2"/>
          <input data-testid="portal-pay-note" value={payForm.note} onChange={e=>setPayForm({...payForm, note:e.target.value})} placeholder="Note (optional)" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm mb-2"/>
          <button data-testid="portal-pay-submit" onClick={reportPayment} className="de-btn-primary w-full justify-center">Submit payment</button>
        </div>

        {payInfo.items.length > 0 && (
          <div className="border-t border-[#E5E7EB] pt-4 mt-4 space-y-2">
            <div className="label-eyebrow">History</div>
            {paymentHistory.map(p=>(
              <div key={p.id} data-testid={`portal-payment-${p.id}`} className="text-sm flex justify-between">
                <span>{money(p.amount)} · <span className="uppercase text-xs">{p.method}</span></span>
                <span className={`text-xs font-bold uppercase ${p.payment_status==="paid"?"text-green-700":"text-[#C79A3B]"}`}>{p.payment_status==="paid"?"Confirmed":"Awaiting confirmation"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="de-card p-6" data-testid="portal-documents-card">
        <div className="label-eyebrow mb-3">Documents</div>
        {documents.length === 0 && <div className="text-sm text-[#6B7280]">No documents yet. Your invoice and payment receipts will appear here once issued.</div>}
        <div className="space-y-2">
          {documents.map(d => (
            <a key={d.id} data-testid={`portal-doc-${d.id}`} href={`${api.defaults.baseURL}/portal/document/${d.id}`} target="_blank" rel="noreferrer"
               className="flex items-center gap-2 border border-[#E5E7EB] px-3 py-2.5 text-sm bg-white hover:border-[#12263A] transition-colors">
              <FileText size={16} className="text-[#C79A3B] shrink-0"/>
              <span className="flex-1 truncate">{d.filename}</span>
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">{d.type}</span>
            </a>
          ))}
        </div>
      </div>

      <Link to="/blog" data-testid="portal-insights-link"
        className="de-card p-6 flex items-center gap-4 hover:border-[#C79A3B] transition-colors group">
        <div className="w-11 h-11 rounded-full bg-[#FBF3EC] text-[#C79A3B] flex items-center justify-center shrink-0">
          <BookOpen size={20}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow mb-0.5">Insights &amp; Guides</div>
          <div className="text-sm text-[#12263A] font-bold">Planning, build tips &amp; project advice</div>
          <div className="text-xs text-[#6B7280] mt-0.5">Practical reading from the team who&rsquo;s building your extension.</div>
        </div>
        <ArrowRight size={16} className="text-[#C79A3B] shrink-0 group-hover:translate-x-0.5 transition-transform"/>
      </Link>
    </div>
  );
}
